/**
 * Furniture store — manages placed furniture instances for the presence radar.
 *
 * All positions are stored as fractions (0..1) relative to the canvas size so
 * that items remain correctly placed when the canvas is resized.
 */
import { get, writable } from 'svelte/store';
import type { PlacedFurniture } from 'src/components/Radar/Types';
import { loadFurnituresDUI, saveFurnituresDUI } from 'src/components/Radar/helpers/furnitures-helper';
import { toast } from '@Shelly/shelly-design-system/index';

// ── Stores ───────────────────────────────────────────────────────────────────

export const placedFurnitures = writable<PlacedFurniture[]>([]);
export const selectedFurnitureId = writable<string | null>(null);
export const furnituresVisible = writable<boolean>(true);
export const currentDeviceId = writable<string | null>(null);

// ── Actions ──────────────────────────────────────────────────────────────────

/** Load furniture for a device and set it as the active device. */
export function setCurrentDeviceId(deviceId: string | null): void {
	currentDeviceId.set(deviceId);
	if (deviceId) {
		placedFurnitures.set(loadFurnituresDUI(deviceId));
	} else {
		placedFurnitures.set([]);
	}
	selectedFurnitureId.set(null);
}

/** Select a furniture item by its instance ID (null to deselect). */
export function selectFurniture(id: string | null): void {
	selectedFurnitureId.set(id);
}

/** Add a new placed furniture item and persist it. */
export function addPlacedFurniture(item: PlacedFurniture): void {
	placedFurnitures.update((list) => [...list, item]);
	persistFurnitures();
}

/** Update an existing placed furniture item (matched by id) and persist. */
export function updatePlacedFurniture(updated: PlacedFurniture): void {
	placedFurnitures.update((list) => list.map((f) => (f.id === updated.id ? updated : f)));
	persistFurnitures();
}

/** Delete a placed furniture item by id and persist. */
export function deletePlacedFurniture(id: string): void {
	placedFurnitures.update((list) => list.filter((f) => f.id !== id));
	if (get(selectedFurnitureId) === id) selectedFurnitureId.set(null);
	persistFurnitures();
}

/** Remove all placed furniture for the current device. */
export function resetDroppedFurnitures(): void {
	placedFurnitures.set([]);
	selectedFurnitureId.set(null);
	persistFurnitures();
}

export function toggleFurnituresVisibility(): void {
	furnituresVisible.update((v) => !v);
	toast.open({
		type: 'info',
		title: `Furniture is now ${get(furnituresVisible) ? 'visible' : 'hidden'}.`,
	});
}

// ── Internal ─────────────────────────────────────────────────────────────────

function persistFurnitures(): void {
	const deviceId = get(currentDeviceId);
	if (!deviceId) return;
	saveFurnituresDUI(deviceId, get(placedFurnitures)).catch((err) => {
		console.error('[furnitureStore] Failed to persist furniture:', err);
	});
}

// ── Unique-name helper ───────────────────────────────────────────────────────

/** Returns a unique name for a new item based on the base name. */
export function uniqueName(baseName: string): string {
	const existing = new Set(get(placedFurnitures).map((f) => f.name.toLowerCase()));
	if (!existing.has(baseName.toLowerCase())) return baseName;
	let n = 2;
	for (;;) {
		const candidate = `${baseName} ${n}`;
		if (!existing.has(candidate.toLowerCase())) return candidate;
		n++;
	}
}

// ── Instance ID generator ────────────────────────────────────────────────────

export function makeFurnitureId(): string {
	return `furn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
